import { defineStore } from 'pinia'
import {
  ErrorType,
  WalletException,
  UnspecifiedErrorCode,
  ChainCosmosErrorCode,
  CosmosWalletException
} from '@injectivelabs/exceptions'
import { Wallet, isCosmosWallet } from '@injectivelabs/wallet-base'
import { switchToActiveMetamaskNetwork } from '@/app/wallet/metamask'

type WalletStoreState = {}

const initialStateFactory = (): WalletStoreState => ({})

export const useWalletStore = defineStore('wallet', {
  state: (): WalletStoreState => initialStateFactory(),
  actions: {
    async connect({ wallet, address }: { wallet: Wallet; address?: string }) {
      const walletStore = useWalletStore()
      const jsonStore = useSharedJsonStore()
      const sharedWalletStore = useSharedWalletStore()

      if (wallet === Wallet.Metamask) {
        await sharedWalletStore.connectMetamask()
      }

      if (wallet === Wallet.Keplr) {
        await sharedWalletStore.connectKeplr()
      }

      if (wallet === Wallet.Leap) {
        await sharedWalletStore.connectLeap()
      }

      if ([Wallet.Ledger, Wallet.LedgerLegacy].includes(wallet) && address) {
        await sharedWalletStore.connectLedger({
          wallet,
          address
        })
      }

      if (wallet === Wallet.Phantom) {
        await sharedWalletStore.connectPhantomWallet()
      }

      if (wallet === Wallet.Ninji) {
        await sharedWalletStore.connectNinji()
      }

      if (wallet === Wallet.Cosmostation) {
        await sharedWalletStore.connectCosmosStation()
      }

      if (
        [Wallet.TrezorBip32, Wallet.TrezorBip44].includes(wallet) &&
        address
      ) {
        await sharedWalletStore.connectTrezor({ wallet, address })
      }

      if (wallet === Wallet.BitGet) {
        await sharedWalletStore.connectBitGet()
      }

      if (wallet === Wallet.OkxWallet) {
        await sharedWalletStore.connectOkxWallet()
      }

      if (wallet === Wallet.WalletConnect) {
        await sharedWalletStore.connectWalletConnect()
      }

      if (sharedWalletStore.isUserConnected) {
        const someAddressInWalletIsBlackListed =
          sharedWalletStore.addresses.some(
            (address) =>
              jsonStore.blacklistedAddresses.find(
                (blacklistedAddress) =>
                  blacklistedAddress.toLowerCase() === address.toLowerCase()
              ) !== undefined
          )

        if (someAddressInWalletIsBlackListed) {
          walletStore.disconnect()

          throw new WalletException(
            new Error('Connected account address is restricted.'),
            {
              code: UnspecifiedErrorCode,
              type: ErrorType.WalletError
            }
          )
        }
      }
    },

    async validateAndQueue() {
      const portfolioStore = usePortfolioStore()
      const sharedWalletStore = useSharedWalletStore()

      await sharedWalletStore.validateAndQueue()

      if (
        isCosmosWallet(sharedWalletStore.wallet) &&
        !portfolioStore.hasEnoughInjForGas
      ) {
        throw new CosmosWalletException(
          new Error('Insufficient INJ to pay for gas/transaction fees.'),
          {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextCode: ChainCosmosErrorCode.ErrInsufficientFee
          }
        )
      }
    },

    async switchToActiveNetwork() {
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      if (sharedWalletStore.wallet === Wallet.Metamask) {
        await switchToActiveMetamaskNetwork()
      }
    },

    async disconnect() {
      const appStore = useAppStore()
      const voteStore = useVoteStore()
      const missionStore = useMissionStore()
      const stakingStore = useStakingStore()
      const launchpadStore = useLaunchpadStore()
      const portfolioStore = usePortfolioStore()
      const sharedWalletStore = useSharedWalletStore()

      await sharedWalletStore.logout()

      appStore.reset()
      voteStore.reset()
      missionStore.reset()
      stakingStore.reset()
      portfolioStore.reset()
      launchpadStore.resetUserState()
    }
  }
})
